/**
 * PROVE — receipts, evidence packs, and the audit room.
 *
 * This is the half of the product the customer actually buys. Everything upstream
 * produces verdicts; these three surfaces are what makes a verdict checkable by
 * someone who does not trust us — which is the only definition of "proof" that
 * matters commercially.
 */

import { useMemo, useState } from "react";
import { verifyPackTrustRoot, type OutOfBandKey } from "@excellent/core/verification-trust-root";
import {
  ACTORS,
  ARTIFACTS,
  NOW,
  PACKS,
  RECEIPTS,
  actor,
  control,
  fmtBytes,
  fmtDate,
  fmtDateTime,
  fmtNumber,
  fmtRelative,
  pack as findPack,
  receipt as findReceipt,
  run as findRun,
  type Pack,
  type Receipt,
} from "./data";
import { href } from "./router";
import {
  Badge,
  Button,
  Callout,
  Card,
  Choice,
  Code,
  Dialog,
  Empty,
  MissingObjectPage,
  Field,
  Fields,
  Grid,
  Hash,
  Icon,
  Label,
  PageShell,
  Section,
  SearchBox,
  Select,
  Stat,
  Table,
  Timeline,
  TimelineItem,
  Verdict,
} from "./ui";
import type { Column } from "./ui";
import { packBlockers, useStore } from "./store";

/* ══════════════════════════════════════════════════════════════════════════
   COPY THAT DESCRIBES SHIPPED BEHAVIOUR

   This is a prototype, but it is compiled into the same client bundle as the
   product and served at `/styleguide/verification/*`, so a sentence here is a
   sentence a reader can be shown. Sentences that describe what the ENGINE does
   are therefore held to the same standard as the canonical pages, and are the
   same sentences: `excellent/client/verification/pages/prove.tsx` measured them
   against `excellent/server/verification-prove.ts` in 3d54a87cb.

   Held as module constants rather than inline JSX text because prettier fills
   JSX children at printWidth 100 and would re-wrap a pinned phrase across a
   newline, breaking a literal-space assertion silently.
   ══════════════════════════════════════════════════════════════════════════ */

/** Mirrors `PACKS_LEDE` in the canonical page. The download emits identifiers and
 *  metadata; it carries no receipt envelopes, no evidence bodies, no verifier. */
const PACKS_LEDE =
  "A scoped export of run, control, and receipt identifiers with limitation metadata. Packs do not contain signed receipt envelopes, evidence bodies, or independently obtained trust material.";

/** Mirrors `AUDIT_ROOM_LEDE`. `handleVerificationPackGet` calls `appendLog` zero
 *  times, so a pack READ is never recorded; the `view` row is minted at compose. */
const AUDIT_ROOM_LEDE =
  "Time-limited, read-only access for people outside your organisation. They see scoped packs and nothing else. Grants, revocations, downloads, redactions and expiries are recorded; those rows do not prove that a reviewer accessed a pack.";

/** `appendLog` pushes onto a plain array; `VerificationAccessLogDto` has no
 *  prior-hash field, so the access log is append-only but not a hash chain.
 *  (Evidence LINEAGE is a real chain — `appendLineageEvent` carries
 *  `prevEventHash` — which is why that copy is left alone.) */
const VIEWS_RECORDED_FOOT = "Recorded at compose; rows are not hash-chained";

/** Receipt replay refuses `receipt-integrity-unavailable` when no signed envelope
 *  was retained (3518f8a9a), so an unconditional replay promise overstates it. */
const RECEIPT_EVIDENCE_NOTE =
  "The exact bytes each check read, pinned by hash at issue time. Replaying the receipt later needs a signed envelope retained for it; without one, replay reports that it is unavailable.";

/* ══════════════════════════════════════════════════════════════════════════
   RECEIPTS
   ══════════════════════════════════════════════════════════════════════════ */


/**
 * B3 scenarios, run live against the shipped trust-root engine.
 *
 * The pack, the declared key, the out-of-band key and the signature are IDENTICAL
 * across both rows. The ONLY difference is `verifierProvenance.origin` — whether
 * the verifier that read the pack came from inside the pack or from out-of-band.
 *
 * That isolation is the point: a demo that varied several inputs at once would
 * show two different answers without showing WHY they differ, and the whole of
 * B3 rests on that one variable.
 */
const TRUST_ROOT_PACK = {
  tenantId: "tenant_acme",
  generatedAt: "2026-08-18T00:00:00.000Z",
  declaredKeys: [{ keyId: "key_acme_2026", fingerprint: "sha256:ab12cd34" }],
  signedByKeyId: "key_acme_2026",
} as const;

// `source` must be one of the engine's OUT_OF_BAND_KEY_SOURCES. My first draft
// invented "customer-dns" and the type rejected it — which is the enum doing its
// job: a fabricated key source would have made this demo describe a retrieval
// path the product does not support.
const TRUST_ROOT_OOB_KEYS: OutOfBandKey[] = [
  {
    keyId: "key_acme_2026",
    fingerprint: "sha256:ab12cd34",
    source: "well-known-url",
    retrievedAt: "2026-08-17T00:00:00.000Z",
    revokedAt: null,
  },
];

const TRUST_ROOT_SCENARIOS = [
  {
    label: "Verifier obtained OUT-OF-BAND",
    result: verifyPackTrustRoot({
      pack: TRUST_ROOT_PACK,
      outOfBandKeys: TRUST_ROOT_OOB_KEYS,
      verifierProvenance: {
        verifierId: "excellent-verify",
        origin: "out-of-band",
        fingerprint: "sha256:ver99",
        expectedFingerprint: "sha256:ver99",
      },
    }),
  },
  {
    label: "Verifier shipped INSIDE the pack",
    result: verifyPackTrustRoot({
      pack: TRUST_ROOT_PACK,
      outOfBandKeys: TRUST_ROOT_OOB_KEYS,
      verifierProvenance: {
        verifierId: "excellent-verify",
        origin: "in-pack",
        fingerprint: "sha256:ver99",
        expectedFingerprint: null,
      },
    }),
  },
];

export function ReceiptsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"all" | "PASS" | "FAIL">("all");

  const rows = useMemo(() => {
    let list = RECEIPTS;
    if (status !== "all") list = list.filter((r) => r.status === status);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter(
        (r) => r.id.includes(n) || r.runId.includes(n) || r.payloadHash.includes(n) || r.signatureKeyId.includes(n),
      );
    }
    return [...list].sort((a, b) => new Date(b.issuedAt).getTime() - new Date(a.issuedAt).getTime());
  }, [q, status]);

  const columns: Column<Receipt>[] = [
    {
      key: "id",
      header: "Receipt",
      primary: true,
      sortValue: (r) => r.id,
      render: (r) => (
        <>
          <span className="vp-mono">{r.id}</span>
          {r.supersedes ? (
            <div style={{ marginTop: "calc(var(--spacing) * 1)" }}>
              <Badge tone="waived" glyph="⇢">
                corrects {r.supersedes}
              </Badge>
            </div>
          ) : null}
        </>
      ),
    },
    {
      key: "run",
      header: "Run",
      sortValue: (r) => r.runId,
      render: (r) => <span className="vp-mono">{r.runId}</span>,
    },
    {
      key: "status",
      header: "Verdict",
      sortValue: (r) => r.status,
      render: (r) => <Verdict status={r.status} />,
    },
    {
      key: "checks",
      header: "Checks",
      sortValue: (r) => r.checkCount,
      render: (r) => <span className="vp-mono">{r.checkCount}</span>,
    },
    {
      key: "hash",
      header: "Payload hash",
      render: (r) => <Hash value={r.payloadHash} onCopy={() => store.toast("Payload hash copied.")} />,
    },
    // No "Signing key" column. Eight columns overflowed a laptop viewport and
    // pushed "Offline replay" and "Issued" off-screen — and the key is uniform
    // across a period, so it earned its place least. It is on the detail page,
    // where it matters.
    {
      key: "replay",
      header: "Offline replay",
      sortValue: (r) => (r.replayVerified ? 0 : 1),
      render: (r) =>
        r.replayVerified ? (
          <Badge tone="pass" glyph="✓">
            verified
          </Badge>
        ) : (
          <Badge tone="unknown" glyph="?">
            unchecked
          </Badge>
        ),
    },
    {
      key: "issued",
      header: "Issued",
      sortValue: (r) => new Date(r.issuedAt).getTime(),
      render: (r) => <span className="vp-mono">{fmtRelative(r.issuedAt)}</span>,
    },
  ];

  return (
    <PageShell
      wide
      eyebrow="Prove"
        title="Receipts"
        lede="The append-only ledger. Each receipt binds one run's verdict to the exact evidence hashes, policy versions and engine build that produced it, signed with your key."
    >

      {/* B3 — run by the SHIPPED trust-root engine, both directions. */}
      <Section
        title="Trust root"
        note="verifyPackTrustRoot from @excellent/core/verification-trust-root — the same function test-verification-trust-root gates."
      >
        <Card>
          <p className="vp-row-sub">
            B3 is not &ldquo;the pack verifies&rdquo;. It is that the pack verifies with a verifier
            obtained <strong>out-of-band</strong>, against fingerprints published{" "}
            <strong>outside the pack</strong>. Those are different claims, so both are run below and
            the engine states the difference in its own words.
          </p>
          <ul>
            {TRUST_ROOT_SCENARIOS.map((sc) => (
              <li key={sc.label}>
                <strong>{sc.label}</strong> — <span className="vp-mono">{sc.result.mode}</span>
                {sc.result.usedKeySource ? (
                  <> · key source <span className="vp-mono">{sc.result.usedKeySource}</span></>
                ) : null}
                {/* The engine's sentence verbatim. A UI paraphrase here would be a
                    second opinion the gate does not cover. */}
                <div className="vp-row-sub">{sc.result.statement}</div>
                {sc.result.findings.length > 0 ? (
                  <div className="vp-row-sub">
                    {sc.result.findings.map((f) => (
                      <div key={f.code}>
                        <span className="vp-mono">{f.severity}</span> {f.message}
                      </div>
                    ))}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <p className="vp-row-sub">
            The two rows differ only in where the VERIFIER came from — the pack, the keys and the
            signature are identical. If both ever read the same, this surface has stopped
            distinguishing the thing B3 is about.
          </p>
        </Card>
      </Section>

      <Callout tone="neutral" icon="info" title="Corrections append; they never overwrite.">
        <span className="vp-mono">rcp_8812b</span> below corrects <span className="vp-mono">rcp_8812</span>{" "}
        — a stale status field made the July sweep miscount three deactivated accounts. Both receipts remain
        readable and the correction links to its parent, because a ledger you can edit is not evidence of
        anything.
      </Callout>

      <div className="vp-filters" style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
        <SearchBox value={q} onChange={setQ} placeholder="Search by receipt, run, hash or key…" />
        <Select
          label="Verdict"
          value={status}
          onChange={setStatus}
          options={[
            { value: "all", label: "All verdicts" },
            { value: "PASS", label: "PASS only" },
            { value: "FAIL", label: "FAIL only" },
          ]}
        />
        <span className="vp-filter-spacer" />
        <span className="vp-result-count">
          {rows.length} of {RECEIPTS.length}
        </span>
      </div>

      <Card flush>
        <Table
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => onNavigate(href("receipts", r.id))}
          empty={<Empty title="No receipts match">Clear the filters to see the whole ledger.</Empty>}
        />
      </Card>
    </PageShell>
  );
}

export function ReceiptDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const r = findReceipt(id);

  if (!r) {
    return (
      <MissingObjectPage title={`No receipt ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("receipts"))}>
          Back to the ledger
        </Button>
      </MissingObjectPage>
    );
  }

  const run = findRun(r.runId);
  const child = RECEIPTS.find((x) => x.parentReceiptId === r.id);

  return (
    <PageShell
      wide
      eyebrow="Receipt"
        title={r.id}
        lede={`Issued ${fmtDateTime(r.issuedAt)} for run ${r.runId} by engine ${r.engineVersion}.`}
        actions={
          <>
        <Button
              icon="refresh"
              onClick={() =>
                store.toast("Replayed from the pinned evidence hashes — verdict re-derived identically.")
              }
            >
              Verify offline
        </Button>
        <Button variant="primary" icon="arrow-right" onClick={() => onNavigate(href("runs", r.runId))}>
              Open the run
        </Button>
          </>
        }
    >

      <div className="vp-inline" style={{ marginBottom: "calc(var(--spacing) * 4.5)" }}>
        <Verdict status={r.status} size="lg" />
        <Badge tone={r.replayVerified ? "pass" : "unknown"} size="lg" glyph={r.replayVerified ? "✓" : "?"}>
          {r.replayVerified ? "replays offline" : "replay unchecked"}
        </Badge>
        <Badge tone="neutral" size="lg">
          {r.checkCount} checks
        </Badge>
      </div>

      {r.supersedes ? (
        <Callout tone="waived" icon="alert" title={`This receipt corrects ${r.supersedes}.`}>
          <p style={{ margin: "0 0 calc(var(--spacing) * 2)" }}>{r.correctionReason}</p>
          <Button
            size="sm"
            variant="outline"
            icon="arrow-right"
            onClick={() => onNavigate(href("receipts", r.supersedes!))}
          >
            Read the original
          </Button>
        </Callout>
      ) : null}

      {child ? (
        <Callout tone="unknown" icon="alert" title={`Superseded by ${child.id}.`}>
          This receipt is still valid as a record of what was concluded at the time, but a later correction
          exists. Any pack containing this receipt also contains the correction, and the replay tool follows
          the link.
          <div style={{ marginTop: "calc(var(--spacing) * 2)" }}>
        <Button size="sm" variant="outline" icon="arrow-right" onClick={() => onNavigate(href("receipts", child.id))}>
              Read the correction
        </Button>
          </div>
        </Callout>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <div className="vp-stack">
          <Card
            title="What this receipt binds"
            subtitle="A receipt is only as good as the things it pins. These are them."
          >
            <Timeline>
              <TimelineItem
                tone="accent"
                glyph="1"
                title="The claim"
                meta={run?.label ?? r.runId}
                note="What was asserted and verified, as a run over a named set of controls."
              />
              <TimelineItem
                tone="accent"
                glyph="2"
                title={`${r.evidenceHashes.length} evidence hashes`}
                note={RECEIPT_EVIDENCE_NOTE}
              />
              <TimelineItem
                tone="accent"
                glyph="3"
                title={`${r.policyVersionRefs.length} policy versions`}
                note="Which rules were in force. A later policy change cannot retroactively alter what this run decided."
              />
              <TimelineItem
                tone="accent"
                glyph="4"
                title={`Engine ${r.engineVersion}`}
                note="The build that evaluated the policy. Bug-for-bug reproducibility requires knowing this."
              />
              <TimelineItem
                tone="pass"
                glyph="5"
                title={`Signed with ${r.signatureKeyId}`}
                meta={`${r.signatureAlgorithm} · customer-held key`}
                note="Excellent does not hold this key and cannot issue a receipt on your behalf."
              />
            </Timeline>
          </Card>

          <Card title="Evidence hashes" subtitle="Pinned at issue time." flush>
            <div className="vp-rows">
              {r.evidenceHashes.map((h) => {
                const match = artifactByHash(h);
                return (
                  <div key={h} className="vp-row">
                    <span className="vp-row-main">
                      <span className="vp-row-title">
                        {match ? match.label : <span className="vp-muted">artifact not in this fixture</span>}
                      </span>
                      <span className="vp-row-sub vp-mono" style={{ fontSize: "var(--type-subtext-size)" }}>
                        {h}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      {match ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          icon="arrow-right"
                          onClick={() => onNavigate(href("evidence", match.id))}
                        >
                          Open
                        </Button>
                      ) : null}
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>

          <Card
            title="Offline verification"
            subtitle="What someone runs to check this without us. No network, no Excellent service."
          >
            <Code>{`$ excellent-verify --pack pkt_0031.tar.zst --receipt ${r.id}

reading manifest ........................ ok
checking ${r.evidenceHashes.length} evidence hashes ................ ok
re-deriving ${r.checkCount} check results ............. ok
comparing to recorded verdict ........... ok  (${r.status})
verifying signature (${r.signatureKeyId}) .. ok

VERIFIED — this receipt's recorded verdict (${r.status}) is
reproducible from the packed evidence alone.`}</Code>
            <p className="vp-help">
              The verifier is a static binary shipped inside the pack. It has no credentials and makes no
              network calls, so the claim it confirms does not depend on our servers being up — or on us being
              honest.
            </p>
          </Card>
        </div>

        <aside className="vp-split-aside">
          <Card title="Receipt record">
            <Fields>
              <Field label="Receipt id">
                <span className="vp-mono">{r.id}</span>
              </Field>
              <Field label="Run">
                <button
                  type="button"
                  className="vp-crumb"
                  style={{ color: "var(--accent)" }}
                  onClick={() => onNavigate(href("runs", r.runId))}
                >
                  {r.runId}
                </button>
              </Field>
              <Field label="Verdict">
                <Verdict status={r.status} />
              </Field>
              <Field label="Payload hash">
                <Hash value={r.payloadHash} onCopy={() => store.toast("Payload hash copied.")} />
              </Field>
              <Field label="Algorithm">
                <span className="vp-mono">{r.signatureAlgorithm}</span>
              </Field>
              <Field label="Key">
                <span className="vp-mono">{r.signatureKeyId}</span>
              </Field>
              <Field label="Parent">
                {r.parentReceiptId ? <span className="vp-mono">{r.parentReceiptId}</span> : "— (root)"}
              </Field>
              <Field label="Issued">{fmtDateTime(r.issuedAt)}</Field>
              <Field label="Replay checked">
                {r.replayCheckedAt ? fmtDateTime(r.replayCheckedAt) : "never"}
              </Field>
            </Fields>
          </Card>

          <Card title="Policy versions bound" flush>
            <div className="vp-rows">
              {r.policyVersionRefs.map((pid) => (
                <button
                  key={pid}
                  type="button"
                  className="vp-row"
                  data-clickable="true"
                  onClick={() => onNavigate(href("policies", pid))}
                >
                  <span className="vp-row-main">
                    <span className="vp-row-title vp-mono">{pid}</span>
                  </span>
                  <span className="vp-row-aside">
                    <Icon name="chevron-right" size={14} />
                  </span>
                </button>
              ))}
            </div>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}

/**
 * Match a receipt's pinned evidence hash back to the artifact that carries it.
 *
 * A receipt stores HASHES, not artifact ids — that is the point, since the hash is
 * what makes the binding verifiable without our database. So the UI has to resolve
 * the other way to show a human-readable label, and it has to tolerate a miss: a
 * hash whose artifact has aged out of retention still verifies, and the row says
 * so rather than rendering blank.
 */
const ARTIFACT_BY_HASH = new Map(ARTIFACTS.map((a) => [a.contentHash, a]));

function artifactByHash(hash: string) {
  return ARTIFACT_BY_HASH.get(hash);
}

/* ══════════════════════════════════════════════════════════════════════════
   EVIDENCE PACKS
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Evidence packs.
 *
 * ── The compose dialog is the argument ──
 *
 * Composing a pack is the moment the product either tells the truth or does not.
 * The dialog therefore shows what WILL BE EXCLUDED before you export, names each
 * excluded control and why, and makes "carry it as a stated limitation" a distinct,
 * explicitly-labelled choice from "leave it out". A one-click "export everything"
 * button would quietly produce a document that implies coverage the evidence does
 * not support — which is the failure mode this whole product is built against.
 */
export function PacksPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [composing, setComposing] = useState(false);
  const blockers = packBlockers(store.findings, store.controls);

  const columns: Column<Pack>[] = [
    {
      key: "label",
      header: "Pack",
      primary: true,
      sortValue: (p) => p.label,
      render: (p) => (
        <>
          {p.label}
          <div className="vp-mono" style={{ color: "var(--ink-3)", marginTop: "calc(var(--spacing) * 0.5)", fontSize: "var(--type-subtext-size)" }}>
            {p.id} · {p.kind.replace(/_/g, " ")}
          </div>
        </>
      ),
    },
    {
      key: "controls",
      header: "Controls",
      sortValue: (p) => p.controlIds.length,
      render: (p) => <span className="vp-mono">{p.controlIds.length}</span>,
    },
    {
      key: "receipts",
      header: "Receipts",
      sortValue: (p) => p.receiptIds.length,
      render: (p) => <span className="vp-mono">{p.receiptIds.length}</span>,
    },
    {
      key: "limits",
      header: "Stated limitations",
      sortValue: (p) => p.scopeLimitations.length,
      render: (p) =>
        p.scopeLimitations.length === 0 ? (
          <span className="vp-muted">none</span>
        ) : (
          <Badge tone="unknown">{p.scopeLimitations.length}</Badge>
        ),
    },
    // Size is on the detail page — see the note on the receipts table above.
    {
      key: "verified",
      header: "Offline",
      render: (p) =>
        p.offlineVerified ? (
          <Badge tone="pass" glyph="✓">
            replays
          </Badge>
        ) : (
          <Badge tone="unknown" glyph="?">
            unchecked
          </Badge>
        ),
    },
    {
      key: "shared",
      header: "Shared with",
      render: (p) =>
        p.sharedWith.length === 0 ? (
          <span className="vp-muted">nobody</span>
        ) : (
          <Badge tone="accent">
            {p.sharedWith.length} reviewer{p.sharedWith.length === 1 ? "" : "s"}
          </Badge>
        ),
    },
    {
      key: "created",
      header: "Created",
      sortValue: (p) => new Date(p.createdAt).getTime(),
      render: (p) => <span className="vp-mono">{fmtRelative(p.createdAt)}</span>,
    },
  ];

  return (
    <PageShell
      wide
      eyebrow="Prove"
        title="Evidence packs"
        lede={PACKS_LEDE}
        actions={
          <Button variant="primary" icon="package" onClick={() => setComposing(true)}>
            Compose a pack
          </Button>
        }
    >

      {blockers.length > 0 ? (
        <Callout
          tone="unknown"
          icon="info"
          title={`${blockers.length} of ${store.controls.length} controls cannot be included today.`}
        >
          You can still compose and hand over a pack — it will just represent {store.controls.length - blockers.length}{" "}
          controls as verified and state the rest as limitations. That is a real, usable artifact, and it is not
          the same thing as a clean opinion.
        </Callout>
      ) : null}

      <Card flush>
        <Table columns={columns} rows={PACKS} rowKey={(p) => p.id} onRowClick={(p) => onNavigate(href("packs", p.id))} />
      </Card>

      {composing ? <ComposeDialog onClose={() => setComposing(false)} onNavigate={onNavigate} /> : null}
    </PageShell>
  );
}

function ComposeDialog({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  onNavigate: (to: string) => void;
}) {
  const store = useStore();
  const [handling, setHandling] = useState<"exclude" | "state" | null>("state");
  const [audience, setAudience] = useState("act_ext");

  const blockers = packBlockers(store.findings, store.controls);
  const includable = store.controls.filter((c) => c.status === "PASS");

  return (
    <Dialog
      title="Compose an evidence pack"
      subtitle="Choose what it claims. The pack will say exactly this, and nothing more."
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="package"
            disabled={!handling}
            onClick={() => {
              // Deliberately does NOT navigate. It used to open `pkt_0031` — a
              // pack composed that morning by someone else — which read as "here
              // is the pack you just made" and was the one no-op in the prototype
              // that actively misled rather than simply doing nothing.
              store.toast(
                `Prototype: a pack would be composed and signed here — ${includable.length} controls verified, ${blockers.length} ${handling === "state" ? "carried as stated limitations" : "excluded"}.`,
              );
              onClose();
            }}
          >
            Compose and sign
          </Button>
        </>
      }
    >
      <div className="vp-form">
        <div>
          <Label>What can be represented as verified</Label>
          <div className="vp-card" style={{ boxShadow: "none" }}>
            <div className="vp-rows">
              {includable.map((c) => (
                <div key={c.id} className="vp-row">
                  <span className="vp-row-main">
                    <span className="vp-row-title">
                      <span className="vp-mono" style={{ color: "var(--ink-3)", marginRight: "calc(var(--spacing) * 2)" }}>
                        {c.ref}
                      </span>
                      {c.title}
                    </span>
                  </span>
                  <span className="vp-row-aside">
                    <Verdict status={c.status} />
                  </span>
                </div>
              ))}
            </div>
          </div>
          <p className="vp-help">
            {includable.length} of {store.controls.length} controls carry a current receipt whose verdict
            replays offline.
          </p>
        </div>

        <div>
          <Label required>What to do about the rest</Label>
          <div className="vp-card" style={{ boxShadow: "none", marginBottom: "calc(var(--spacing) * 2.5)" }}>
            <div className="vp-rows">
              {blockers.map((b) => (
                <div key={b.controlId} className="vp-row">
                  <span className="vp-row-main">
                    <span className="vp-row-title">
                      <span className="vp-mono" style={{ color: "var(--ink-3)", marginRight: "calc(var(--spacing) * 2)" }}>
                        {b.ref}
                      </span>
                      {control(b.controlId)?.title}
                    </span>
                    <span className="vp-row-sub">{b.reason}</span>
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="vp-stack" data-gap="sm">
            <Choice
              selected={handling === "state"}
              title="Carry them as stated limitations (recommended)"
              desc="The pack lists each one with its status and reason. An auditor sees the gap and can scope their own testing around it. Nothing is hidden and nothing is claimed."
              onSelect={() => setHandling("state")}
            />
            <Choice
              selected={handling === "exclude"}
              title="Exclude them entirely"
              desc="The pack covers only the verified controls and says so in its scope statement. Use this when the recipient asked for a specific subset — not to make the coverage look better than it is."
              onSelect={() => setHandling("exclude")}
            />
          </div>
        </div>

        <div>
          <Label>Share with</Label>
          <Select
            label="Share with"
            value={audience}
            onChange={setAudience}
            options={[
              { value: "none", label: "Nobody yet — compose only" },
              ...ACTORS.filter((a) => a.role === "auditor").map((a) => ({
                value: a.id,
                label: `${a.name} — read-only, 7 days`,
              })),
            ]}
          />
          <p className="vp-help">
            External access is read-only and expires. Every view and download is logged to a hash chain the
            reviewer cannot alter.
          </p>
        </div>
      </div>
    </Dialog>
  );
}

export function PackDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const p = findPack(id);

  if (!p) {
    return (
      <MissingObjectPage title={`No pack ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("packs"))}>
          Back to packs
        </Button>
      </MissingObjectPage>
    );
  }

  return (
    <PageShell
      wide
      eyebrow={`Evidence pack · ${p.kind.replace(/_/g, " ")}`}
        title={p.label}
        lede={`Composed ${fmtDateTime(p.createdAt)} by ${actor(p.createdByActorId).name}. ${p.expiresAt ? `Access expires ${fmtDate(p.expiresAt)}.` : "No expiry."}`}
        actions={
          <>
        <Button icon="refresh" onClick={() => store.toast("Offline replay run — all receipts re-derived identically.")}>
              Verify offline
        </Button>
        <Button variant="primary" icon="download" onClick={() => store.toast("Prototype: the pack would download.")}>
              Download
        </Button>
          </>
        }
    >

      <Grid cols={4}>
        <Stat label="Controls" value={p.controlIds.length} foot="Represented in this pack" />
        <Stat label="Receipts" value={p.receiptIds.length} foot="Each independently verifiable" />
        <Stat
          label="Stated limitations"
          value={p.scopeLimitations.length}
          tone={p.scopeLimitations.length > 0 ? "unknown" : "pass"}
          foot={p.scopeLimitations.length > 0 ? "Carried in the open" : "No gaps to state"}
        />
        <Stat
          label="Offline replay"
          value={p.offlineVerified ? "Verified" : "Unchecked"}
          tone={p.offlineVerified ? "pass" : "unknown"}
          foot={`${fmtBytes(p.sizeBytes)} · ${p.redactionCount} redactions applied`}
        />
      </Grid>

      {p.scopeLimitations.length > 0 ? (
        <div style={{ marginTop: "calc(var(--spacing) * 5)" }}>
          <Card
            title="What this pack does NOT claim"
            subtitle="Printed on the pack's first page, not buried in an appendix."
          >
            <div className="vp-stack" data-gap="sm">
              {p.scopeLimitations.map((l, i) => (
                <Callout key={i} tone="unknown" icon="alert">
                  {l}
                </Callout>
              ))}
            </div>
          </Card>
        </div>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <div className="vp-stack">
          <Card title="Contents" subtitle="Everything a recipient needs to check this without contacting us." flush>
            <div className="vp-rows">
              {[
                { n: "manifest.json", d: "Every file with its hash, plus the pack's own bundle hash.", i: "file" as const },
                { n: `receipts/ (${p.receiptIds.length})`, d: "Signed verdicts with their policy and evidence bindings.", i: "seal" as const },
                { n: `evidence/ (${p.artifactCount})`, d: "The exact artifacts the receipts cite, redacted where required.", i: "book" as const },
                { n: "policies/", d: "The policy versions in force at run time, as evaluated.", i: "file" as const },
                { n: "decisions/", d: "Review decisions with their rationales and the actor who made each.", i: "inbox" as const },
                { n: "excellent-verify", d: "A static offline verifier. No network, no credentials.", i: "cpu" as const },
                { n: "SCOPE.md", d: "What is covered, what is not, and why — in plain language.", i: "flag" as const },
              ].map((f) => (
                <div key={f.n} className="vp-row">
                  <span style={{ flex: "0 0 auto", color: "var(--ink-3)" }}>
                    <Icon name={f.i} size={15} />
                  </span>
                  <span className="vp-row-main">
                    <span className="vp-row-title vp-mono">{f.n}</span>
                    <span className="vp-row-sub">{f.d}</span>
                  </span>
                </div>
              ))}
            </div>
          </Card>

          <Card title="Receipts included" flush>
            <div className="vp-rows">
              {p.receiptIds.map((rid) => {
                const r = findReceipt(rid);
                if (!r) return null;
                return (
                  <button
                    key={rid}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("receipts", rid))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title vp-mono">{rid}</span>
                      <span className="vp-row-sub">
                        {findRun(r.runId)?.label} · issued {fmtDate(r.issuedAt)}
                        {r.supersedes ? ` · corrects ${r.supersedes}` : ""}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Verdict status={r.status} />
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>

          {p.sharedWith.length > 0 ? (
            <Card
              title="Access log"
              subtitle="Hash-chained. A reviewer cannot view a document without the view being recorded."
            >
              <Timeline>
                {p.sharedWith.map((s) => (
                  <TimelineItem
                    key={s.actorId}
                    tone="accent"
                    glyph="→"
                    title={`Granted to ${actor(s.actorId).name}`}
                    meta={`${fmtDateTime(s.grantedAt)} · read-only · expires ${fmtDate(s.expiresAt)}`}
                    note={`${s.views} views, ${s.downloads} downloads recorded to date.`}
                  />
                ))}
              </Timeline>
            </Card>
          ) : null}
        </div>

        <aside className="vp-split-aside">
          <Card title="Pack record">
            <Fields>
              <Field label="Pack id">
                <span className="vp-mono">{p.id}</span>
              </Field>
              <Field label="Kind">{p.kind.replace(/_/g, " ")}</Field>
              <Field label="Bundle hash">
                <Hash value={p.bundleHash} onCopy={() => store.toast("Bundle hash copied.")} />
              </Field>
              <Field label="Size">{fmtBytes(p.sizeBytes)}</Field>
              <Field label="Artifacts">{p.artifactCount}</Field>
              <Field label="Redactions">{p.redactionCount}</Field>
              <Field label="Composed by">{actor(p.createdByActorId).name}</Field>
              <Field label="Expires">{p.expiresAt ? fmtDate(p.expiresAt) : "never"}</Field>
            </Fields>
          </Card>

          <Card title="Controls represented" flush>
            <div className="vp-rows">
              {p.controlIds.map((cid) => {
                const c = control(cid);
                if (!c) return null;
                return (
                  <button
                    key={cid}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("controls", cid))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">
                        <span className="vp-mono" style={{ color: "var(--ink-3)", marginRight: "calc(var(--spacing) * 1.5)" }}>
                          {c.ref}
                        </span>
                        {c.family}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Verdict status={c.status} />
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   AUDIT ROOM
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The audit room.
 *
 * Two things are on this page that a normal sharing UI leaves out, because for
 * this product they are the feature rather than the fine print: the expiry is a
 * countdown rather than a date buried in a table, and the access log is presented
 * as a hash chain with its verification state shown. An external reviewer's
 * activity being tamper-evident is part of what the customer is buying.
 */
export function AuditRoomPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const shared = PACKS.filter((p) => p.sharedWith.length > 0);
  const reviewers = ACTORS.filter((a) => a.role === "auditor");

  return (
    <PageShell
      eyebrow="Prove"
        title="Audit room"
        lede={AUDIT_ROOM_LEDE}
        actions={
          <Button variant="primary" icon="plus" onClick={() => store.toast("Prototype: the invite flow would open.")}>
            Invite a reviewer
          </Button>
        }
    >

      <Grid cols={3}>
        <Stat label="Active grants" value={shared.reduce((s, p) => s + p.sharedWith.length, 0)} tone="accent" />
        <Stat
          label="Views recorded"
          value={shared.reduce((s, p) => s + p.sharedWith.reduce((x, g) => x + g.views, 0), 0)}
          foot={VIEWS_RECORDED_FOOT}
        />
        <Stat
          label="Access logs"
          value={shared.reduce((s, p) => s + p.sharedWith.reduce((x, g) => x + g.views, 0), 0)}
        />
      </Grid>

      <Section title="External reviewers" note="What each one can see, and for how long.">
        {reviewers.map((r) => {
          const grants = shared.flatMap((p) =>
            p.sharedWith.filter((g) => g.actorId === r.id).map((g) => ({ pack: p, grant: g })),
          );
          return (
            <Card
              key={r.id}
              title={r.name}
              subtitle={r.email}
              actions={
                grants.length > 0 ? (
                  <Badge tone="pass" glyph="✓">
                    active
                  </Badge>
                ) : (
                  <Badge tone="neutral">no active grant</Badge>
                )
              }
              footer={
                <>
                  <span>
                    Role <span className="vp-mono">auditor</span> · read-only · cannot see controls outside a
                    granted pack, cannot see any finding still open
                  </span>
                  {grants.length > 0 ? (
                    <span style={{ marginLeft: "auto" }}>
                      <Button
                        size="sm"
                        variant="reject"
                        icon="x"
                        onClick={() => store.toast("Prototype: access would be revoked immediately.")}
                      >
                        Revoke access
                      </Button>
                    </span>
                  ) : null}
                </>
              }
            >
              {grants.length === 0 ? (
                <p className="vp-tiny" style={{ margin: "0" }}>
                  This reviewer has no current access. Grant it by sharing a pack.
                </p>
              ) : (
                <div className="vp-rows" style={{ margin: "calc(var(--spacing) * -4)" }}>
                  {grants.map(({ pack: sharedPack, grant }) => {
                    const daysLeft = Math.ceil(
                      (new Date(grant.expiresAt).getTime() - NOW.getTime()) /
                        86_400_000,
                    );
                    return (
                      <button
                        key={sharedPack.id}
                        type="button"
                        className="vp-row"
                        data-clickable="true"
                        onClick={() => onNavigate(href("packs", sharedPack.id))}
                      >
                        <span className="vp-row-main">
                          <span className="vp-row-title">{sharedPack.label}</span>
                          <span className="vp-row-sub">
                            {sharedPack.controlIds.length} controls · {sharedPack.receiptIds.length} receipts ·{" "}
                            {grant.views} views, {grant.downloads} downloads
                          </span>
                        </span>
                        <span className="vp-row-aside">
                          <Badge tone={daysLeft <= 2 ? "unknown" : "accent"} glyph="⏱">
                            {daysLeft}d left
                          </Badge>
                        </span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </Section>

      <Section title="What a reviewer sees" note="The same evidence, none of the workspace.">
        <Card>
          <Fields>
            <Field label="Can see">
              The packs shared with them, the receipts inside, the evidence those receipts cite, the review
              decisions with rationales, and the offline verifier.
            </Field>
            <Field label="Cannot see">
              Any control outside a granted pack. Any open finding. Any other tenant. Your connector
              credentials, scopes, or source configuration. Anything after their grant expires.
            </Field>
            <Field label="Cannot do">
              Write anything at all. There is no reviewer-side mutation in the API surface — the auditor role
              carries no <span className="vp-mono">:write</span> scope, so it is not a UI restriction that a
              crafted request could get around.
            </Field>
            <Field label="Is recorded">
              Every view and download, with actor, timestamp, and object, appended to a hash chain. The chain
              is included in the pack, so the reviewer's own activity is part of the evidence.
            </Field>
          </Fields>
        </Card>
      </Section>
    </PageShell>
  );
}
